// Taxes report component - 2024 - Pagination, 10 items per page, labels updated
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { taxesAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatDateDDMMYYYY } from '../utils/dateUtils';

interface TaxBreakdownItem {
  tax_rate: number;
  total_quantity: number;
  total_revenue: number;
  total_tax_amount: number;
}

interface TaxReport {
  date: string;
  summary: {
    total_orders: number;
    total_items_sold: number;
    total_revenue: number;
    total_tax_amount: number;
  };
  breakdown: TaxBreakdownItem[];
}

const Taxes: React.FC = () => {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [selectedReport, setSelectedReport] = useState<TaxReport | null>(null);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [editingNoteDate, setEditingNoteDate] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showMonthModal, setShowMonthModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [exporting, setExporting] = useState(false);
  
  // Son günlerin raporları (daha fazla yüklenecek)
  const [reports, setReports] = useState<TaxReport[]>([]);

  // Notları veritabanından yükle
  const loadNotes = async () => {
    try {
      const notesData = await taxesAPI.getNotes();
      setNotes(notesData);
    } catch (error) {
      console.error('Notlar yüklenemedi:', error);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  // Not kaydet
  const saveNote = async (date: string, note: string) => {
    try {
      await taxesAPI.saveNote(date, note);
      const newNotes = { ...notes, [date]: note };
      setNotes(newNotes);
      setEditingNoteDate(null);
      toast.success('Not kaydedildi');
    } catch (error: any) {
      toast.error(`Not kaydedilemedi: ${error.response?.data?.error || error.message}`);
    }
  };

  // Not sil
  const deleteNote = async (date: string) => {
    try {
      await taxesAPI.deleteNote(date);
      const newNotes = { ...notes };
      delete newNotes[date];
      setNotes(newNotes);
      toast.success('Not silindi');
    } catch (error: any) {
      toast.error(`Not silinemedi: ${error.response?.data?.error || error.message}`);
    }
  };

  // Günün detaylı raporunu göster
  const showDayDetails = async (date: string) => {
    setSelectedDate(date);
    setLoadingSelected(true);
    try {
      const report = await taxesAPI.getTaxReport(date);
      setSelectedReport(report);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      toast.error(`Rapor yüklenemedi: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoadingSelected(false);
    }
  };

  // Son günlerin raporlarını yükle (daha fazla gün için)
  const loadReports = async () => {
    setLoading(true);
    try {
      const dates: string[] = [];
      // Daha fazla gün yükle (sayfalama için)
      for (let i = 0; i < 100; i++) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        dates.push(date.toISOString().split('T')[0]);
      }

      const reportsData = await Promise.all(
        dates.map(async (date) => {
          try {
            const report = await taxesAPI.getTaxReport(date);
            return report;
          } catch {
            return null;
          }
        })
      );

      setReports(reportsData.filter(r => r !== null) as TaxReport[]);
    } catch (error) {
      console.error('Raporlar yüklenemedi:', error);
      toast.error('Raporlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Seçilen tarihin raporunu yükle
  const loadSelectedDateReport = async () => {
    if (!selectedDate) {
      toast.error('Lütfen bir tarih seçin');
      return;
    }

    setLoadingSelected(true);
    try {
      const report = await taxesAPI.getTaxReport(selectedDate);
      setSelectedReport(report);
    } catch (error: any) {
      toast.error(`Rapor yüklenemedi: ${error.response?.data?.error || error.message}`);
      setSelectedReport(null);
    } finally {
      setLoadingSelected(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  // Vergi oranına göre tutarı bul
  const getAmountByTaxRate = (report: TaxReport, taxRate: number): number => {
    const item = report.breakdown.find(
      (item) => Math.abs(item.tax_rate - taxRate) < 0.01
    );
    return item ? item.total_revenue : 0;
  };

  // Toplam hesapla
  const getTotal = (reports: TaxReport[]): number => {
    return reports.reduce((sum, r) => sum + r.summary.total_revenue, 0);
  };

  // Vergi oranına göre toplam hesapla
  const getTotalByTaxRate = (reports: TaxReport[], taxRate: number): number => {
    return reports.reduce((sum, r) => sum + getAmountByTaxRate(r, taxRate), 0);
  };

  // Aylık rapor indir
  const exportMonthlyReport = async (year: number, month: number) => {
    try {
      setExporting(true);
      toast.loading('Rapor hazırlanıyor...', { id: 'export' });
      const blob = await taxesAPI.exportMonthlyReport(year, month);
      
      // Blob'u indir
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vergi_raporu_${year}_${String(month).padStart(2, '0')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Rapor başarıyla indirildi', { id: 'export' });
      setShowMonthModal(false);
    } catch (error: any) {
      toast.error(`Rapor indirilemedi: ${error.response?.data?.error || error.message}`, { id: 'export' });
    } finally {
      setExporting(false);
    }
  };

  // Ay seçim modalını aç
  const openMonthModal = () => {
    const today = new Date();
    setSelectedYear(today.getFullYear());
    setSelectedMonth(today.getMonth() + 1);
    setShowMonthModal(true);
  };

  // Sayfalama hesaplamaları
  const totalPages = Math.ceil(reports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedReports = reports.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
          {/* Geri Dönüş Butonları */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-dark-border">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium flex items-center gap-2"
            >
              ← Geri
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="bg-cafe-brown text-white px-4 py-2 rounded-lg hover:bg-cafe-dark transition-colors text-sm font-medium flex items-center gap-2"
            >
              ⚙️ Admin Paneli
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
            >
              🏠 Hızlı Sipariş
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
              <span className="mr-3">📊</span>
              Vergi Raporları
            </h1>
            
            {/* Tarih Seçici ve Butonlar */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  📅 Tarih:
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-gray-100 text-sm"
                />
              </div>
              <button
                onClick={loadSelectedDateReport}
                disabled={loadingSelected}
                className="bg-cafe-brown text-white px-4 py-2 rounded-lg hover:bg-cafe-dark transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingSelected ? '⏳ Yükleniyor...' : '👁️ Görüntüle'}
              </button>
              <button
                onClick={openMonthModal}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                📥 Aylık Rapor İndir
              </button>
            </div>
          </div>
        </div>

        {/* Ay Seçim Modalı */}
        {showMonthModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                  📅 Ay Seçin
                </h2>
                <button
                  onClick={() => setShowMonthModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Yıl Seçimi */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Yıl
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 bg-white dark:bg-dark-bg text-gray-900 dark:text-gray-100"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Ay Seçimi - Küçük Kutucuklar */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Ay
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
                    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
                  ].map((monthName, index) => {
                    const monthNum = index + 1;
                    const isSelected = selectedMonth === monthNum;
                    return (
                      <button
                        key={monthNum}
                        onClick={() => setSelectedMonth(monthNum)}
                        className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                          isSelected
                            ? 'bg-cafe-brown text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {monthName}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowMonthModal(false)}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors font-medium"
                >
                  İptal
                </button>
                <button
                  onClick={() => exportMonthlyReport(selectedYear, selectedMonth)}
                  disabled={exporting}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {exporting ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      İndiriliyor...
                    </>
                  ) : (
                    <>
                      📥 İndir
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Seçilen Tarihin Detaylı Raporu */}
        {selectedReport && (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                📋 {formatDateDDMMYYYY(selectedReport.date)} Detaylı Rapor
              </h2>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Toplam Sipariş</p>
                <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                  {selectedReport.summary.total_orders}
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-600 dark:text-green-400 mb-1">Toplam Gelir</p>
                <p className="text-2xl font-bold text-green-800 dark:text-green-200">
                  {formatPrice(selectedReport.summary.total_revenue)}
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Toplam KDV</p>
                <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
                  {formatPrice(selectedReport.summary.total_tax_amount)}
                </p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                <p className="text-sm text-orange-600 dark:text-orange-400 mb-1">Satılan Adet</p>
                <p className="text-2xl font-bold text-orange-800 dark:text-orange-200">
                  {selectedReport.summary.total_items_sold}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      Vergi Oranı
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      Satılan Adet
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      Toplam Gelir
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      KDV Tutarı
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                  {[0, 6, 12, 21].map((rate) => {
                    const item = selectedReport.breakdown.find(
                      (item) => Math.abs(item.tax_rate - rate) < 0.01
                    );
                    
                    if (!item && rate !== 0 && rate !== 6 && rate !== 12 && rate !== 21) return null;
                    
                    return (
                      <tr key={rate} className="hover:bg-gray-50 dark:hover:bg-dark-bg">
                        <td className="py-3 px-4 text-gray-800 dark:text-gray-200 font-medium">
                          %{rate}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                          {item ? item.total_quantity : 0}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-800 dark:text-gray-200">
                          {formatPrice(item ? item.total_revenue : 0)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-orange-600 dark:text-orange-400">
                          {formatPrice(item ? item.total_tax_amount : 0)}
                        </td>
                      </tr>
                    );
                  })}
                  
                  {/* Diğer vergi oranları */}
                  {selectedReport.breakdown
                    .filter(
                      (item) => ![0, 6, 12, 21].some((rate) => Math.abs(item.tax_rate - rate) < 0.01)
                    )
                    .map((item) => (
                      <tr key={`other-${item.tax_rate}`} className="hover:bg-gray-50 dark:hover:bg-dark-bg bg-yellow-50 dark:bg-yellow-900/20">
                        <td className="py-3 px-4 text-gray-800 dark:text-gray-200 font-medium">
                          %{item.tax_rate.toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                          {item.total_quantity}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-gray-800 dark:text-gray-200">
                          {formatPrice(item.total_revenue)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-orange-600 dark:text-orange-400">
                          {formatPrice(item.total_tax_amount)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Günler Tablosu */}
        {loading ? (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-brown mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Raporlar yükleniyor...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      #
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      Tarih
                    </th>
                    <th className="text-left py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      Not
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      Toplam
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      0%
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      6%
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      12%
                    </th>
                    <th className="text-right py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      21%
                    </th>
                    <th className="text-center py-4 px-6 text-sm font-bold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-dark-border">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                  {paginatedReports.map((report, index) => (
                    <tr
                      key={report.date}
                      className="hover:bg-gray-50 dark:hover:bg-dark-bg transition-colors"
                    >
                      <td className="py-4 px-6 text-gray-700 dark:text-gray-300">
                        {startIndex + index + 1}
                      </td>
                      <td className="py-4 px-6 text-gray-800 dark:text-gray-200 font-medium">
                        {formatDateDDMMYYYY(report.date)}
                      </td>
                      <td className="py-4 px-6">
                        {editingNoteDate === report.date ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              defaultValue={notes[report.date] || ''}
                              onBlur={async (e) => {
                                if (e.target.value.trim()) {
                                  await saveNote(report.date, e.target.value.trim());
                                } else {
                                  setEditingNoteDate(null);
                                }
                              }}
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  if (e.currentTarget.value.trim()) {
                                    await saveNote(report.date, e.currentTarget.value.trim());
                                  } else {
                                    setEditingNoteDate(null);
                                  }
                                } else if (e.key === 'Escape') {
                                  setEditingNoteDate(null);
                                }
                              }}
                              autoFocus
                              className="flex-1 border border-gray-300 dark:border-dark-border rounded px-2 py-1 text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-gray-100"
                              placeholder="Not ekle..."
                            />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-700 dark:text-gray-300 text-sm">
                              {notes[report.date] || '-'}
                            </span>
                            <button
                              onClick={() => setEditingNoteDate(report.date)}
                              className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                              title="Not ekle/düzenle"
                            >
                              {notes[report.date] ? '✏️' : '➕'}
                            </button>
                            {notes[report.date] && (
                              <button
                                onClick={() => deleteNote(report.date)}
                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                                title="Notu sil"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6 text-right font-semibold text-gray-800 dark:text-gray-200">
                        {formatPrice(report.summary.total_revenue)}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700 dark:text-gray-300">
                        {formatPrice(getAmountByTaxRate(report, 0))}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700 dark:text-gray-300">
                        {formatPrice(getAmountByTaxRate(report, 6))}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700 dark:text-gray-300">
                        {formatPrice(getAmountByTaxRate(report, 12))}
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700 dark:text-gray-300">
                        {formatPrice(getAmountByTaxRate(report, 21))}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() => showDayDetails(report.date)}
                          className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                        >
                          Görüntüle
                        </button>
                      </td>
                    </tr>
                  ))}
                  
                  {paginatedReports.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-400">
                        Veri bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Sayfalama */}
            {reports.length > itemsPerPage && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-dark-border">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Toplam {reports.length} kayıt, Sayfa {currentPage} / {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Önceki
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded ${
                          currentPage === pageNum
                            ? 'bg-cafe-brown text-white'
                            : 'border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Sonraki
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Taxes;
